// @flow

import React from 'react';
import { connect } from 'react-redux';
import BoardEditModal from 'app/common/react/components/BoardEdit/BoardEditModal';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import ProfileBoardCardWithImpressions from 'app/common/react/components/profile/ProfileBoardCard/ProfileBoardCardWithImpressions';
import type { BoardSortOptionsType } from 'app/packages/pinterest-flow-types/user';
import type { Dispatch } from 'redux';
import type { Invite } from 'app/common/react/components/BoardCollab/types';
import type { ProfileBoardCardLayout } from 'app/common/react/components/profile/ProfileBoardCard/layout';
import type { State as BoardType } from 'app/common/redux/reducers/board';
import { Box } from 'gestalt';
import { interpolateNamedTemplate } from 'app/common/lib/react/interpolate';
import { startTiming } from 'app/common/lib/TraceLogger/logTraceEvent';
import { track } from 'app/common/lib/react/analytics';
import { updateBoardWithFieldSetKey } from 'app/common/redux/actions/board/update';
import PrettyDate, { DateFormatType } from 'app/common/react/i18n/PrettyDate';
import type {
  ImageType,
  ImagePositionType,
  ProfileBoardType,
} from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';

const BOARD_ACCESS_WRITE = 'write';
const COVER_IMG_HEIGHT = 200;
const COVER_IMG_WIDTH = 200;

type Props = {|
  board: ProfileBoardType,
  boardOrder?: BoardSortOptionsType,
  images?: {
    '170x': ?Array<ImageType>,
  },
  invite?: Invite,
  isMergeTarget: boolean,
  isOwnProfile: boolean,
  isReordering: boolean,
  isUserBlocked?: boolean,
  layout: 'list' | ProfileBoardCardLayout,
  onBoardArchiveChange?: () => void,
  onBoardDelete?: (board: BoardType) => void,
  onBoardLeave?: (boardId: string) => void,
  onBoardPrivacyChange?: () => void,
  onBoardUpdate?: (id: string, updatedFields: Object) => void,
  progress: ?number,
  slotIndex: number,
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  updateBoard: () => *,
|};

type State = {|
  coverImagePos: ImagePositionType,
  coverImageUrl: string,
  isEditModalOpen: boolean,
|};

export class ProfileBoardCardContainer extends React.Component<Props, State> {
  static defaultProps = {
    isMergeTarget: false,
    isOwnProfile: false,
    isReordering: false,
  };

  constructor(props: Props) {
    super(props);

    const coverImageUrl = props.board.has_custom_cover
      ? props.board.image_cover_hd_url || props.board.image_cover_url
      : '';

    this.state = {
      coverImagePos: {
        height: 0,
        width: 0,
        x: props.board.has_custom_cover ? props.board.cover_pin.crop[0] : 0,
        y: props.board.has_custom_cover ? props.board.cover_pin.crop[1] : 0,
      },
      coverImageUrl,
      isEditModalOpen: false,
    };
  }

  UNSAFE_componentWillReceiveProps = (nextProps: Props) => {
    const currentBoardId = this.props.board.id;
    if (currentBoardId === nextProps.board.id) {
      if (
        !!this.props.progress &&
        this.props.progress < 1 &&
        (!nextProps.progress || nextProps.progress === 1)
      ) {
        nextProps.updateBoard(); // TODO(jackhsu): move to redux middleware
      }
      return;
    }

    // should only update when moving around.
    const nextCoverImgUrl = nextProps.board.image_cover_hd_url || nextProps.board.image_cover_url;
    const currentCoverImgUrl = this.state.coverImageUrl;

    if (nextProps.board.has_custom_cover) {
      if (nextCoverImgUrl !== currentCoverImgUrl) {
        this.setState({
          coverImageUrl: nextCoverImgUrl,
        });
      }
    } else {
      this.setState({ coverImageUrl: '' });
    }
  };

  getSubtitleText = () => {
    const { board, boardOrder, layout } = this.props;
    const inListView = layout === 'list';
    if (board.archived_by_me_at) {
      const timeSince = i18n.getHumanizedTimesince(board.archived_by_me_at);
      return inListView ? (
        <span>{timeSince}</span>
      ) : (
        interpolateNamedTemplate(
          i18n._('Archived {{ timeSince }}', 'subtitle for archived boards on profile page'),
          {
            timeSince,
          },
        )
      );
    } else if (boardOrder && (boardOrder === 'newest' || boardOrder === 'oldest')) {
      return inListView ? (
        <span>
          <PrettyDate value={new Date(board.created_at)} dateFormatType={DateFormatType.LONG} />
        </span>
      ) : null;
    } else if ((boardOrder && boardOrder === 'last_pinned_to') || inListView) {
      const timeSince = i18n.getHumanizedTimesince(board.board_order_modified_at);
      return inListView ? <span>{timeSince}</span> : null;
    }
    return null;
  };

  handleBoardCardClick = () => {
    startTiming(PCONST.thrift.TracingClientTypes.PWTAction.BOARD_PAGE_LOAD);

    const { board } = this.props;

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_BOARD,
      element: PCONST.thrift.ElementType.BOARD_COVER,
      objectId: board.id,
      view: PCONST.thrift.ViewType.USER,
    });
  };
  // TODO(AWON): pull coverImgUrl + coverImagePos from redux
  handleCoverImageChange = (coverImageUrl: string, coverImagePos: ImagePositionType) => {
    this.setState({ coverImageUrl, coverImagePos });
  };

  // TODO(xixia): ultimately remove 'any' for the flowtype.
  handleEditClick = (e: any) => {
    e.preventDefault();
    e.stopPropagation();

    const { board } = this.props;

    this.setState({ isEditModalOpen: true });

    track(PCONST.thrift.EventType.CLICK, {
      component: PCONST.thrift.ComponentType.FLOWED_BOARD,
      element: PCONST.thrift.ElementType.BOARD_EDIT_BUTTON,
      objectId: board.id,
      view: PCONST.thrift.ViewType.USER,
    });
  };

  handleEditClose = () => {
    this.setState({ isEditModalOpen: false });
  };

  render() {
    if (!this.props.board) {
      return null;
    }
    const {
      board,
      images,
      isMergeTarget,
      isReordering,
      isOwnProfile,
      layout,
      onBoardArchiveChange,
      onBoardDelete,
      onBoardPrivacyChange,
      onBoardLeave,
      onBoardUpdate,
      progress,
      slotIndex,
      isUserBlocked,
    } = this.props;
    const { image_cover_url, image_cover_hd_url, url } = board;
    const { coverImagePos, coverImageUrl, isEditModalOpen } = this.state;
    const canEdit = isOwnProfile || board.access.includes(BOARD_ACCESS_WRITE);

    const subtitle = this.getSubtitleText();

    const toastImageUrl = image_cover_url || image_cover_hd_url || '';

    return (
      <Box>
        <NavigateLink onClick={this.handleBoardCardClick} to={url}>
          <ProfileBoardCardWithImpressions
            board={board}
            canEdit={canEdit}
            coverImagePos={coverImagePos}
            coverImageUrl={coverImageUrl}
            images={images || board.images}
            isMergeTarget={isMergeTarget}
            isReordering={isReordering}
            isOwnProfile={isOwnProfile}
            layout={layout}
            onEditClick={this.handleEditClick}
            progress={progress}
            slotIndex={slotIndex}
            subtitle={subtitle}
            toastImageUrl={toastImageUrl}
            isUserBlocked={isUserBlocked}
          />
        </NavigateLink>
        {isEditModalOpen && (
          <BoardEditModal
            boardId={board.id}
            coverImageHeight={COVER_IMG_HEIGHT}
            coverImageWidth={COVER_IMG_WIDTH}
            onBoardArchiveChange={onBoardArchiveChange}
            onBoardDelete={onBoardDelete}
            onBoardPrivacyChange={onBoardPrivacyChange}
            onBoardLeave={onBoardLeave}
            onBoardUpdate={onBoardUpdate}
            onClose={this.handleEditClose}
            onCropChange={this.handleCoverImageChange}
          />
        )}
      </Box>
    );
  }
}

// $FlowFixMe: new errors found from flow 0.96 upgrade
export default connect(
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  ({ blockingActions, boards, ui }, props): {| images: *, progress: * |} => ({
    images: (boards.content[props.board.id] || {}).images,
    // we should use the updated pin and section counts from the redux store
    progress: ((blockingActions.boardMap[props.board.id] || {}).wholeBoard || {}).progress,
  }),
  // $FlowFixMe Flow v0.104.0 upgrade supression.
  (dispatch: Dispatch<any>, props): {| updateBoard: * |} => ({
    updateBoard: () => dispatch(updateBoardWithFieldSetKey(props.board.id, 'profile_grid_item')),
  }),
)(ProfileBoardCardContainer);
